class TabsCustom extends HTMLElement {
  /** @type {HTMLElement[]} */
  get tabs() {
    return [...this.querySelectorAll('[role="tab"]')];
  }

  /** @type {HTMLElement[]} */
  get panels() {
    return [...this.querySelectorAll('[role="tabpanel"]')];
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });

    // Activate first tab by default
    const tabs = this.tabs;
    if (tabs.length > 0 && !tabs.some((tab) => tab.getAttribute('aria-selected') === 'true')) {
      this.#activateTab(tabs[0]);
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    const tab = /** @type {HTMLElement} */ (event.target).closest('[role="tab"]');
    if (tab) {
      this.#activateTab(tab);
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.getAttribute('role') !== 'tab') return;

    const tabs = this.tabs;
    const index = tabs.indexOf(target);
    if (index === -1) return;

    let newIndex;

    switch (event.key) {
      case 'ArrowRight':
        newIndex = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        newIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activateTab(tabs[newIndex]);
    tabs[newIndex].focus();
  };

  /**
   * @param {HTMLElement} activeTab
   */
  #activateTab(activeTab) {
    const panelId = activeTab.getAttribute('aria-controls');

    for (const tab of this.tabs) {
      tab.setAttribute('aria-selected', String(tab === activeTab));
      tab.setAttribute('tabindex', tab === activeTab ? '0' : '-1');
    }

    for (const panel of this.panels) {
      panel.hidden = panel.id !== panelId;
    }
  }
}

if (!customElements.get('tabs-custom')) {
  customElements.define('tabs-custom', TabsCustom);
}
